import _ from 'lodash'

/**
 * Scroll-position memory for the paginated product listings (ProductListV2, SearchResults
 * and ProductList), so a browser back from a product page returns the user to the row they
 * were looking at, not just to the right page number.
 *
 * The position cannot be restored by the router at navigation time: the listings fetch
 * their products asynchronously, so the grid is still hidden behind `v-if="dataLoaded &&
 * !loading"` and the container has no height to scroll through. Instead the views save
 * their position on the way out and re-apply it themselves once the products have
 * rendered; this module holds the shared plumbing for both halves.
 */

const STORAGE_PREFIX = 'listing-scroll:'

// The app does not scroll the window - #app-layout is a full-height flex column with
// overflow hidden and the page scrolls inside <main id="page-content">. The window is only
// a fallback for any layout that does not render that element.
const SCROLL_CONTAINER_ID = 'page-content'

// How many animation frames a restore keeps re-applying its offset for. The grid can still
// be growing when the first attempt runs (images sizing the rows), which would otherwise
// clamp the scroll short of where the user actually was.
const RESTORE_FRAMES = 15

// Set by the router's scrollBehavior, which is only handed a savedPosition on back/forward
// (popstate) navigations. Restoring is gated on it so that entering a listing normally
// still starts at the top.
let popstateFullPath = null

function getScrollContainer() {
  return document.getElementById(SCROLL_CONTAINER_ID)
}

function getStorage() {
  try {
    return window.sessionStorage
  } catch (error) {
    // Session storage can be blocked entirely (private browsing, cookies disabled).
    // Restoring scroll is a nicety, never worth throwing over.
    return null
  }
}

function storageKey(fullPath) {
  return `${STORAGE_PREFIX}${fullPath}`
}

/**
 * Record where the listing at `fullPath` is scrolled to, on the way out of it.
 * Session storage keeps the entry scoped to this tab and survives a reload of whatever
 * page the user moved on to.
 *
 * @param {String} fullPath - the listing's $route.fullPath (carries ?page= after round 1)
 */
export function saveListingScrollPosition(fullPath) {
  const storage = getStorage()
  if (!storage || !fullPath) return

  const container = getScrollContainer()
  const top = container ? container.scrollTop : window.scrollY

  try {
    storage.setItem(storageKey(fullPath), String(top))
  } catch (error) {
    // Storage quota exceeded - nothing to do.
  }
}

/**
 * Whether a position was recorded for `fullPath`. Lets the router tell a listing route
 * (which restores its own scroll, asynchronously) from every other route without having to
 * keep a list of route names in step.
 *
 * @param {String} fullPath
 * @returns {Boolean}
 */
export function hasListingScrollPosition(fullPath) {
  const storage = getStorage()
  if (!storage || !fullPath) return false

  return storage.getItem(storageKey(fullPath)) !== null
}

/**
 * Flag `fullPath` as having been arrived at via back/forward, which is what allows the
 * listing view to restore its saved position once it has rendered.
 *
 * @param {String} fullPath
 */
export function markListingScrollRestore(fullPath) {
  popstateFullPath = fullPath
}

/**
 * Read and clear the position saved for `fullPath`, returning it only when the router
 * flagged this navigation as a back/forward one. The entry is consumed either way, so a
 * later fresh navigation to the same URL cannot inherit a stale offset.
 *
 * @param {String} fullPath
 * @returns {Number|null} - the offset to scroll back to (0 is a valid one), or null when
 *   there is nothing saved to restore
 */
export function takeListingScrollPosition(fullPath) {
  const storage = getStorage()
  if (!storage || !fullPath) return null

  const isRestoring = popstateFullPath === fullPath
  popstateFullPath = null

  let stored = null
  try {
    stored = storage.getItem(storageKey(fullPath))
    storage.removeItem(storageKey(fullPath))
  } catch (error) {
    return null
  }

  if (!isRestoring || stored === null) return null

  // A stored 0 is a real position - the user was at the top of the listing when they left
  // it - so it has to restore like any other offset. Only a missing entry (handled above)
  // means "nothing to restore".
  const top = _.toInteger(stored)

  return top > 0 ? top : 0
}

/**
 * Put the scroll container back at the top, for when a listing renders with nothing to
 * restore (a fresh visit, a new search, a new filter).
 *
 * #page-content is a single element that the layout reuses across every route rather than
 * re-creating, so it holds on to whatever offset it was last left at. Without this, new
 * results render underneath a container that is still scrolled to where the previous view
 * was.
 */
export function scrollListingToTop() {
  const container = getScrollContainer()

  if (container) {
    container.scrollTop = 0
    return
  }

  window.scrollTo(0, 0)
}

/**
 * Scroll back to `top`, re-applying it over the next few frames so a grid that is still
 * settling cannot clamp the scroll short of where the user was.
 *
 * @param {Number} top
 */
export function applyListingScrollPosition(top) {
  const container = getScrollContainer()
  let framesLeft = RESTORE_FRAMES

  const scrollStep = () => {
    if (container) {
      container.scrollTop = top
    } else {
      window.scrollTo(0, top)
    }

    framesLeft -= 1
    const reached = (container ? container.scrollTop : window.scrollY) >= top

    if (!reached && framesLeft > 0) {
      window.requestAnimationFrame(scrollStep)
    }
  }

  window.requestAnimationFrame(scrollStep)
}
